import { SCREENS, STATUS_CODES } from "apps/login/util/constants";
import {
	CREATE_TSP_TOKEN,
	SEND_OTP_CODE as SEND_OTP_CODE_MUTATION,
	CREATE_REFRESH_TOKEN,
	LOG_IN_WITH_SMS,
	getToken,
	getOTPSendSuccess,
	getTspRefreshToken,
	getUserid,
	getStatusCode,
	getSmsLoginResponse,
	USER_SEND_OTP_SMS,
	getUserSendOTPSuccess,
	USER_VALIDATE_SEND_OTP_SMS,
	getUserTspRefreshToken,
	getPlatformForSms,
} from "apps/login/util/mutations";
import { trackSmsCodeSent, trackSmsCodeReSent, trackSmsCodeVertificationResponse } from "analytics/sms";
import {
	changeScreen,
	_goHome,
	submitLoginError,
	SUBMIT_LOGIN_FAIL,
	SHOW_REACTIVATE,
	SUBMIT_LOGIN_SUCCESS,
	SHOW_SMS_2_FACTOR,
	SHOW_TWO_FACTOR,
	SHOW_CAPTCHA,
} from "./login";
import { getDeviceId } from "shared/util/deviceId";
import { SEND_CODE_FAILED_ERROR_MESSAGE } from "shared/constants/sms";
import _str from "shared/util/_str";

// action types
// ----------------------------------------------------------------------------

export const SUBMIT_SMS_LOGIN = "SMS_LOGIN.SUBMIT_SMS_LOGIN";
export const SET_TSP_ACCESS_TOKEN = "SMS_LOGIN.SET_TSP_ACCESS_TOKEN";
export const SEND_OTP_CODE_SUCCESS = "SMS_LOGIN.SEND_OTP_CODE_SUCCESS";
export const RESEND_OTP_CODE_SUCCESS = "SMS_LOGIN.SEND_OTP_CODE_SUCCESS";
export const SEND_OTP_CODE_FAILED = "SMS_LOGIN.SEND_OTP_CODE_FAILED";
export const RESEND_OTP_CODE = "SMS_LOGIN.RESEND_OTP_CODE";
export const SET_SMS_CODE_VALIDATION_ERROR = "SMS_LOGIN.SET_SMS_CODE_VALIDATION_ERROR";


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	error: "",
	hasCodeValidationError: false,
	hasCodeSendError: false,
	isSubmitting: false,
	isSendingCode: false,
	isSmsTwoFactor: false,
};

// reducer
// ----------------------------------------------------------------------------

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case SUBMIT_SMS_LOGIN:
			return {
				...state,
				isSubmitting: true,
				error: "",
				hasCodeValidationError: false,
			};

		case RESEND_OTP_CODE:
			return {
				...state,
				isSendingCode: true,
				hasCodeValidationError: false,
				hasCodeSendError: false,
			};

		case SEND_OTP_CODE_SUCCESS:
		case RESEND_OTP_CODE_SUCCESS:
			return {
				...state,
				isSendingCode: false,
				isSubmitting: false,
			};

		case SEND_OTP_CODE_FAILED:
			return {
				...state,
				isSendingCode: false,
				isSubmitting: false,
				hasCodeSendError: true,
			};

		case SUBMIT_LOGIN_SUCCESS:
			return {
				...state,
				isSendingCode: false,
				isSubmitting: false,
				isSmsTwoFactor: false,
			};

		case SET_SMS_CODE_VALIDATION_ERROR:
			return {
				...state,
				hasCodeValidationError: true,
				isSendingCode: false,
				isSubmitting: false,
			};

		case SUBMIT_LOGIN_FAIL:
			return {
				...state,
				isSendingCode: false,
				isSubmitting: false,
			};

		case SHOW_SMS_2_FACTOR:
			return {
				...state,
				isSmsTwoFactor: true,
			};

		default:
			return state;
	}
}

// Helpers
const handleMutationError = (error) => ({ success: false, error });

const createTspTokenMutation = (apolloClient) => apolloClient.mutate({ mutation: CREATE_TSP_TOKEN })
	.then(
		(res) => {
			const tspAccessToken = getToken(res);
			return { success: !!tspAccessToken, tspAccessToken };
		},
		handleMutationError,
	);

const sendOtpMutation = (apolloClient, variables) => apolloClient.mutate({
	mutation: SEND_OTP_CODE_MUTATION,
	variables,
})
	.then(
		(res) => ({ success: getOTPSendSuccess(res) }),
		handleMutationError,
	);

const sendOtpForUseridMutation = (apolloClient, variables) => apolloClient.mutate({
	mutation: USER_SEND_OTP_SMS,
	variables,
})
	.then(
		(res) => ({ success: getUserSendOTPSuccess(res), res }),
		handleMutationError,
	);


// action creators
// ----------------------------------------------------------------------------
export const submitSmsLogin = () => ({
	type: SUBMIT_SMS_LOGIN,
});

export const setCodeValidationError = () => ({
	type: SET_SMS_CODE_VALIDATION_ERROR,
});

export const sendOTPCodeFailed = () => ({
	type: SEND_OTP_CODE_FAILED,
});


async function loginWithSms({ apolloClient, tspRefreshToken, dispatch }) {
	const deviceId = await getDeviceId();

	const loginResponse = await apolloClient.mutate({
		mutation: LOG_IN_WITH_SMS,
		variables: {
			input: { tspRefreshToken, deviceId },
		},
	})
		.then(
			(res) => {
				const { reenableAuthCode, reenableUserid } = getSmsLoginResponse(res);

				return {
					success: !!getUserid(res),
					statusCode: getStatusCode(res),
					reenableAuthCode,
					reenableUserid,
				};
			},
			handleMutationError,
		);

	const { statusCode } = loginResponse;

	if (statusCode === STATUS_CODES.DISABLED) {
		dispatch({
			type: SHOW_REACTIVATE,
			authcode: loginResponse.reenableAuthCode,
			userid: loginResponse.reenableUserid,
		});
		return;
	} else if (statusCode === STATUS_CODES.NEEDS_2FA) {
		dispatch({ type: SHOW_TWO_FACTOR });
		dispatch(changeScreen(SCREENS.LOGIN));
		return;
	} else if (statusCode === STATUS_CODES.NEED_CAPTCHA) {
		dispatch({ type: SHOW_CAPTCHA });
		dispatch(changeScreen(SCREENS.LOGIN));
		return;
	} else if (statusCode === STATUS_CODES.NO_USER) {
		dispatch(changeScreen(SCREENS.LOGIN));
		const noUserError = new Error(_str("No account found. Try signing in with email or Facebook or create an account."));
		dispatch(submitLoginError(noUserError));
		return;
	} else if (!loginResponse.success) {
		dispatch(setCodeValidationError());
		dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
		return;
	}

	dispatch({ type: SUBMIT_LOGIN_SUCCESS });
	dispatch(changeScreen(SCREENS.SMS_SUCCESS));
	_goHome();
}

export function sendOTPCode(apolloClient, dialingCode, phoneNumber) {
	return async (dispatch) => {
		const fullPhone = `${dialingCode}${phoneNumber}`;

		dispatch(submitSmsLogin());
		dispatch(changeScreen(SCREENS.SMS_SENDING_CODE));

		const tokenResponse = await createTspTokenMutation(apolloClient);

		if (!tokenResponse.success) {
			dispatch(submitLoginError(new Error(SEND_CODE_FAILED_ERROR_MESSAGE)));
			dispatch(changeScreen(SCREENS.SMS_PHONE_INPUT));
			return;
		}

		const otpVariables = {
			input: {
				tspAccessToken: tokenResponse.tspAccessToken,
				phoneNumber: fullPhone,
				platform: getPlatformForSms(),
			},
		};

		const codeResponse = await sendOtpMutation(apolloClient, otpVariables);

		if (!codeResponse.success) {
			dispatch(submitLoginError(new Error(SEND_CODE_FAILED_ERROR_MESSAGE)));
			dispatch(changeScreen(SCREENS.SMS_PHONE_INPUT));
			return;
		}

		dispatch({ type: SEND_OTP_CODE_SUCCESS });

		trackSmsCodeSent({ existingUser: true });

		dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
	};
}

export function send2FaOTP(apolloClient) {
	return async (dispatch, getState) => {
		dispatch(changeScreen(SCREENS.SMS_SENDING_CODE, SCREENS.SMS_TWO_FACTOR));

		const { login: { userid } } = getState();

		const tokenResponse = await createTspTokenMutation(apolloClient);

		if (!tokenResponse.success) {
			dispatch(submitLoginError(new Error(SEND_CODE_FAILED_ERROR_MESSAGE)));
			dispatch(changeScreen(SCREENS.SMS_TWO_FACTOR));
			return;
		}

		const tspAccessToken = tokenResponse.tspAccessToken;

		const userSendOtpVariables = {
			input: {
				tspAccessToken,
				userid,
				platform: getPlatformForSms(),
			},
		};

		const codeResponse = await sendOtpForUseridMutation(apolloClient, userSendOtpVariables);

		if (!codeResponse.success) {
			dispatch(submitLoginError(new Error(SEND_CODE_FAILED_ERROR_MESSAGE)));
			dispatch(changeScreen(SCREENS.SMS_TWO_FACTOR));
			return;
		}

		dispatch({ type: SEND_OTP_CODE_SUCCESS });

		trackSmsCodeSent({ existingUser: true });

		dispatch(changeScreen(SCREENS.SMS_CODE_INPUT, SCREENS.SMS_TWO_FACTOR));
	};
}

export function validate2FaOTP(apolloClient, code) {
	return async (dispatch, getState) => {
		const { login: { userid } } = getState();

		dispatch(submitSmsLogin());
		dispatch(changeScreen(SCREENS.SMS_VALIDATING_CODE));

		const tokenResponse = await createTspTokenMutation(apolloClient);

		if (!tokenResponse.success) {
			dispatch(submitLoginError(new Error("Unable to login.")));
			dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
			return;
		}

		const userValidateOTPVariables = {
			input: {
				tspAccessToken: tokenResponse.tspAccessToken,
				userid,
				otp: code,
				platform: getPlatformForSms(),
			},
		};

		const refreshTokenResponse = await apolloClient.mutate({
			mutation: USER_VALIDATE_SEND_OTP_SMS,
			variables: userValidateOTPVariables,
		})
			.then(
				(res) => {
					const tspRefreshToken = getUserTspRefreshToken(res);

					return { success: !!tspRefreshToken, tspRefreshToken };
				},
				handleMutationError,
			);

		if (refreshTokenResponse.success) {
			trackSmsCodeVertificationResponse({
				success: true,
				existingUser: true,
			});
		} else {
			dispatch(setCodeValidationError());
			dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
			trackSmsCodeVertificationResponse({
				success: false,
				error: refreshTokenResponse.error,
				existingUser: true,
			});
			return;
		}

		const { tspRefreshToken } = refreshTokenResponse;
		await loginWithSms({ apolloClient, tspRefreshToken, dispatch });
	};
}

export function submitOTPCodeAndLogin(apolloClient, code) {
	return async (dispatch, getState) => {
		const { phoneNumber, dialingCode } = getState().sms;

		dispatch(submitSmsLogin());
		dispatch(changeScreen(SCREENS.SMS_VALIDATING_CODE));

		const tokenResponse = await createTspTokenMutation(apolloClient);

		if (!tokenResponse.success) {
			dispatch(submitLoginError(new Error("Unable to login.")));
			dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
			return;
		}

		const refreshTokenResponse = await apolloClient.mutate({
			mutation: CREATE_REFRESH_TOKEN,
			variables: {
				input: {
					tspAccessToken: tokenResponse.tspAccessToken,
					phoneNumber: `${dialingCode}${phoneNumber}`,
					otp: code,
					platform: getPlatformForSms(),
				},
			},
		})
			.then(
				(res) => {
					const tspRefreshToken = getTspRefreshToken(res);

					return { success: !!tspRefreshToken, tspRefreshToken };
				},
				handleMutationError,
			);

		if (refreshTokenResponse.success) {
			trackSmsCodeVertificationResponse({
				success: true,
				existingUser: true,
			});
		} else {
			dispatch(setCodeValidationError());
			dispatch(changeScreen(SCREENS.SMS_CODE_INPUT));
			trackSmsCodeVertificationResponse({
				success: false,
				error: refreshTokenResponse.error,
				existingUser: true,
			});
			return;
		}

		const { tspRefreshToken } = refreshTokenResponse;
		await loginWithSms({ apolloClient, tspRefreshToken, dispatch });
	};
}

export const resendOTPCodeSuccess = () => ({ type: RESEND_OTP_CODE_SUCCESS });

export const resendOTPCodeFailed = () => {
	return async (dispatch) => {
		dispatch(submitLoginError(new Error(SEND_CODE_FAILED_ERROR_MESSAGE)));
		dispatch(sendOTPCodeFailed());
	};
};

export const resendOTPCode = () => {
	return async (dispatch) => {
		trackSmsCodeReSent();
		dispatch({ type: RESEND_OTP_CODE });
	};
};



// WEBPACK FOOTER //
// ./src/ducks/sms_login.js